import { useState, useEffect } from "react";
import { Plus, Trash2, Calendar, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import apiClient from "@/lib/api";

const DRILL_TYPES = ["Technical", "Tactical", "Physical", "Match Practice", "Set Pieces"];
const INTENSITIES = ["Low", "Medium", "High"];

const intensityBadge = {
  Low: "bg-green-100 text-green-700",
  Medium: "bg-yellow-100 text-yellow-700",
  High: "bg-red-100 text-red-700",
};

const getToday = () => new Date().toISOString().split("T")[0];

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    date: getToday(),
    duration_minutes: 90,
    drill_type: "Technical",
    intensity: "Medium",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    apiClient
      .get("/sessions")
      .then((r) => setSessions(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openModal = () => {
    setForm({ title: "", date: getToday(), duration_minutes: 90, drill_type: "Technical", intensity: "Medium", notes: "" });
    setError("");
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiClient.post("/sessions", {
        ...form,
        duration_minutes: parseInt(form.duration_minutes) || 90,
      });
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save session");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this session and its attendance records?")) return;
    await apiClient.delete(`/sessions/${id}`).catch(console.error);
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="sessions-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-3xl font-black text-gray-900"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Sessions
          </h1>
          <p className="text-gray-600 mt-1">
            {sessions.length} training session{sessions.length !== 1 ? "s" : ""} logged
          </p>
        </div>
        <Button
          onClick={openModal}
          className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
          data-testid="log-session-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Log Session
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-sm border border-gray-200">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3
            className="text-lg font-bold text-gray-900 mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            No sessions logged yet
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Start logging training sessions to track player progress.
          </p>
          <Button
            onClick={openModal}
            className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
            data-testid="log-first-session-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Log First Session
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="sessions-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Session</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Duration</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Intensity</th>
                <th className="text-right px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`session-row-${s.id}`}
                >
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{s.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{s.date}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">{s.duration_minutes} min</td>
                  <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">{s.drill_type}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded-full ${
                        intensityBadge[s.intensity] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {s.intensity}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to="/dashboard/attendance" state={{ session_id: s.id }}>
                        <button
                          className="p-1.5 text-gray-400 hover:text-green-600 transition-colors"
                          title="Take attendance"
                          data-testid={`attendance-btn-${s.id}`}
                        >
                          <ClipboardCheck className="w-4 h-4" />
                        </button>
                      </Link>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        data-testid={`delete-session-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Log Training Session
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Session Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Tuesday Evening Training"
                required
                className="mt-1"
                data-testid="session-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="session-date-input"
                />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  className="mt-1"
                  data-testid="session-duration-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Drill Type</Label>
                <Select
                  value={form.drill_type}
                  onValueChange={(v) => setForm({ ...form, drill_type: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="session-drill-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DRILL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intensity</Label>
                <Select
                  value={form.intensity}
                  onValueChange={(v) => setForm({ ...form, intensity: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="session-intensity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTENSITIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Session notes..."
                rows={3}
                className="mt-1 resize-none"
                data-testid="session-notes-input"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-sm"
                data-testid="save-session-btn"
              >
                {saving ? "Saving..." : "Log Session"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
